import { useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { InputSwitch } from 'primereact/inputswitch'
import { confirmDialog } from 'primereact/confirmdialog'
import type { ConditionTree } from '@kc3/conversations'

import PageHeader from '../../../components/admin/PageHeader.js'
import ResourceTable, {
  type ColumnDef,
} from '../../../components/admin/ResourceTable.js'
import ResourceDrawer from '../../../components/admin/ResourceDrawer.js'
import { useAdminResource } from '../../../hooks/useAdminApi.js'
import { useRuleFields } from '../../../hooks/useRuleFields.js'
import { useToast } from '../../../hooks/useToast.js'
import ApprovalWorkflowEditor, {
  type WorkflowInput,
} from '../../../components/admin/conversation-engine/ApprovalWorkflowEditor.js'
import type { Level } from '../../../components/admin/conversation-engine/ApprovalLevelsEditor.js'

// ---------------------------------------------------------------------------
// Types — mirror apps/api/src/routes/conversations/admin/approval-workflows.ts
// (approval_workflow_definitions table). `triggerCondition` is persisted as a
// JSON record; the route defaults it to `{}` when omitted.
// ---------------------------------------------------------------------------

export interface ApprovalWorkflow {
  id: string
  name: string
  triggerCondition: ConditionTree | null
  levels: Level[]
  onRejection: WorkflowInput['onRejection']
  onTimeout: WorkflowInput['onTimeout']
  isActive: boolean
}

// Stable module-level empty trigger tree (matches RuleBuilder's empty tree).
const EMPTY_TRIGGER: ConditionTree = { operator: 'AND', conditions: [] }
const DEFAULT_TIMEOUT_MINUTES = 60

/** A fresh draft for the create drawer — one blank level, sensible enum defaults. */
function newWorkflowDraft(): WorkflowInput {
  return {
    name: '',
    triggerCondition: EMPTY_TRIGGER,
    levels: [{ level: 1, timeoutMinutes: DEFAULT_TIMEOUT_MINUTES }],
    onRejection: 'close',
    onTimeout: 'escalate',
    isActive: true,
  }
}

/** Project an existing workflow record into the editable WorkflowInput shape. */
function toDraft(row: ApprovalWorkflow): WorkflowInput {
  return {
    name: row.name,
    triggerCondition: row.triggerCondition ?? EMPTY_TRIGGER,
    levels: row.levels.length > 0 ? row.levels : newWorkflowDraft().levels,
    onRejection: row.onRejection,
    onTimeout: row.onTimeout,
    isActive: row.isActive,
  }
}

/**
 * "Custom" when the workflow has at least one trigger condition, else "Always".
 * The trigger may be an empty object (`{}`, the route default) or an empty
 * AND-branch (`{ operator: 'AND', conditions: [] }`).
 */
function triggerSummary(tree: ConditionTree | null): 'Custom' | 'Always' {
  if (!tree) return 'Always'
  const branch = tree as { conditions?: unknown[] }
  if (Array.isArray(branch.conditions) && branch.conditions.length > 0) {
    return 'Custom'
  }
  return 'Always'
}

const ON_REJECTION_LABELS: Record<WorkflowInput['onRejection'], string> = {
  close: 'Close conversation',
  return_to_agent: 'Return to agent',
  escalate: 'Escalate',
}

const ON_TIMEOUT_LABELS: Record<WorkflowInput['onTimeout'], string> = {
  escalate: 'Escalate',
  auto_approve: 'Auto-approve',
  auto_reject: 'Auto-reject',
}

function describeLevel(lv: Level): string {
  const approver = lv.approverAgentId
    ? 'agent'
    : lv.approverQueueId
      ? 'queue'
      : 'unassigned'
  return `L${lv.level} · ${approver}`
}

/**
 * Client-side draft validity gate — mirrors the few checks the backend Zod
 * schema enforces (CreateBodySchema / LevelSchema) without duplicating it.
 * A draft is valid only when:
 *  - `name` is non-empty after trim,
 *  - `levels` has 1..10 entries,
 *  - every level has exactly one approver set (XOR of agent/queue).
 * Returns the first human-readable problem, or `null` when the draft is valid.
 */
export function validateWorkflowDraft(draft: WorkflowInput): string | null {
  if (draft.name.trim() === '') {
    return 'Workflow name is required'
  }
  if (draft.levels.length < 1 || draft.levels.length > 10) {
    return 'A workflow must have between 1 and 10 approval levels'
  }
  const hasApprover = (id: string | undefined): boolean =>
    id !== undefined && id !== ''
  for (const lv of draft.levels) {
    const agent = hasApprover(lv.approverAgentId)
    const queue = hasApprover(lv.approverQueueId)
    // Exactly one approver (XOR) — neither or both is invalid.
    if (agent === queue) {
      return 'Every approval level must have an approver'
    }
  }
  return null
}

export default function ApprovalWorkflowsPage(): JSX.Element {
  const toast = useToast()
  const { items, loading, create, update, remove } =
    useAdminResource<ApprovalWorkflow>(
      '/admin/conversations/approval-workflows',
      { responseKey: 'rows' },
    )
  // Trigger RuleBuilder catalog — domain 'approval' is server-aliased to the
  // automation field set (UI-2e §0.H).
  const { fields } = useRuleFields('approval')

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<ApprovalWorkflow | null>(null)
  const [draft, setDraft] = useState<WorkflowInput>(newWorkflowDraft)
  const [saving, setSaving] = useState(false)
  // Bumped every time the drawer opens so ApprovalWorkflowEditor (and its
  // mount-initialised ApprovalLevelsEditor) remounts cleanly per opened
  // workflow — required by the editor's remount contract.
  const [editorKey, setEditorKey] = useState(0)

  function openCreate(): void {
    setEditing(null)
    setDraft(newWorkflowDraft())
    setEditorKey((k) => k + 1)
    setDrawerOpen(true)
  }

  function openEdit(row: ApprovalWorkflow): void {
    setEditing(row)
    setDraft(toDraft(row))
    setEditorKey((k) => k + 1)
    setDrawerOpen(true)
  }

  function closeDrawer(): void {
    setDrawerOpen(false)
    setEditing(null)
  }

  async function handleSave(): Promise<void> {
    // Gate on client-side validity before hitting the API — avoids raw 400s
    // with unreadable VALIDATION_FAILED bodies. Keep the drawer open on failure.
    const problem = validateWorkflowDraft(draft)
    if (problem) {
      toast.error('Cannot save workflow', problem)
      return
    }
    const payload = {
      name: draft.name.trim(),
      triggerCondition: draft.triggerCondition,
      levels: draft.levels,
      onRejection: draft.onRejection,
      onTimeout: draft.onTimeout,
      isActive: draft.isActive,
    }
    setSaving(true)
    try {
      if (editing) {
        await update(editing.id, payload)
      } else {
        await create(payload)
      }
      closeDrawer()
    } finally {
      setSaving(false)
    }
  }

  function handleDelete(row: ApprovalWorkflow): void {
    confirmDialog({
      message: `Permanently delete "${row.name}"? This cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => void remove(row.id),
    })
  }

  const columns: ColumnDef<ApprovalWorkflow>[] = [
    { label: 'Name', accessor: 'name' },
    {
      label: 'Levels',
      accessor: (r) => (
        <span className="flex flex-wrap gap-1">
          {r.levels.map((lv) => (
            <span
              key={lv.level}
              data-testid="level-chip"
              className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
            >
              {describeLevel(lv)}
            </span>
          ))}
        </span>
      ),
    },
    {
      label: 'Trigger',
      accessor: (r) => triggerSummary(r.triggerCondition),
    },
    {
      label: 'On rejection',
      accessor: (r) => ON_REJECTION_LABELS[r.onRejection] ?? r.onRejection,
    },
    {
      label: 'On timeout',
      accessor: (r) => (
        <span
          data-testid="timeout-chip"
          className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
        >
          {ON_TIMEOUT_LABELS[r.onTimeout] ?? r.onTimeout}
        </span>
      ),
    },
    {
      label: 'Active',
      accessor: (r) => (
        <InputSwitch
          checked={r.isActive}
          onChange={(e) => void update(r.id, { isActive: Boolean(e.value) })}
        />
      ),
    },
  ]

  return (
    <div className="p-4">
      <PageHeader
        title="Approval Workflows"
        description="Multi-level approval workflows. Trigger conditions decide which conversations require sign-off."
        actions={<Button label="+ New Workflow" onClick={openCreate} />}
      />
      <ResourceTable<ApprovalWorkflow>
        columns={columns}
        rows={items}
        loading={loading}
        onEdit={openEdit}
        onDelete={handleDelete}
        empty={{
          title: 'No approval workflows',
          description:
            'Create a workflow to require approval before certain conversations proceed.',
          action: <Button label="+ New Workflow" onClick={openCreate} />,
        }}
      />
      <ResourceDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={handleSave}
        saving={saving}
        width="wide"
        title={
          editing
            ? `Edit Workflow: ${editing.name}`
            : 'New Approval Workflow'
        }
      >
        <ApprovalWorkflowEditor
          key={editorKey}
          value={draft}
          onChange={setDraft}
          fields={fields}
        />
      </ResourceDrawer>
    </div>
  )
}
